import { Flatfile } from '@flatfile/api-internal'
import {
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Separator,
  SimpleSearchBar,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  Tooltip,
  TooltipTrigger,
  useToast,
} from '@flatfile/baseline'
import { memo, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ResourceList } from '../../components/ResourceList/ResourceList'
import { ResourceListContext, ResourceListContextProvider } from '../../components/ResourceList/ResourceListContext'
import { ResourceRowComponent } from '../../components/ResourceList/types'
import { CustomCopyButton } from '../../components/Shared/CopyToClipboard'
import { NoAccessError } from '../../components/Shared/CustomErrorComponent'
import TrialBanner from '../../components/Shared/TrialBanner'
import { useDynamicHeight } from '../../hooks/useDynamicHeight'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { useIdentity } from '../../hooks/useIdentity'
import { useCollection } from '../../resources/hooks/useCollection'
import useResource from '../../resources/hooks/useResource'
import useResourceList from '../../resources/hooks/useResourceList'
import { useCreateResource, useDeleteResource, useUpdateResource } from '../../resources/hooks/useResourceMutation'
import getCollection from '../../resources/support/getCollection'
import { logger } from '../../utils/logging'
import {
  eventTopics,
  getAgentIconSet,
  GroupType,
  library,
  Agent as LibraryAgent,
  libraryGroups,
  teasers,
} from './AgentUtils'
import { DownloadAgentModal } from './DownloadAgentModal'

// Focused search component - memoized wrapper around SimpleSearchBar to prevent focus loss
const FocusedSearchBar = memo(
  ({
    onSearch,
    initialValue = '',
    placeholder = 'Search agents by slug or id...',
    size = 'sm',
    className = 'w-[300px]',
  }: {
    onSearch: (value: string) => void
    initialValue?: string
    placeholder?: string
    size?: 'sm' | 'md'
    className?: string
  }) => {
    const [localSearchValue, setLocalSearchValue] = useState(initialValue)
    const searchRef = useRef<HTMLInputElement>(null)

    // Debounce search to avoid excessive updates
    useEffect(() => {
      const timeoutId = setTimeout(() => {
        onSearch(localSearchValue.trim())
      }, 300)
      return () => clearTimeout(timeoutId)
    }, [localSearchValue, onSearch])

    // Handle local state changes
    const handleSearchChange = useCallback((value: string) => {
      setLocalSearchValue(value)
    }, [])

    return (
      <SimpleSearchBar
        ref={searchRef}
        searchTerm={localSearchValue}
        onSearchChange={handleSearchChange}
        placeholder={placeholder}
        size={size}
        className={className}
      />
    )
  }
)

FocusedSearchBar.displayName = 'FocusedSearchBar'

export const AgentsPage = () => {
  const { addToRefsArray, listHeight } = useDynamicHeight()
  const { environment: activeEnvironment } = useActiveEnvironment()
  const { hasPermission } = useIdentity()
  const [activeTab, setActiveTab] = useState<string>('deployed')
  const [agentTypeFilter, setAgentTypeFilter] = useState<string>('agents')
  const [pageCount, setPageCount] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const pagination = { currentPage, setCurrentPage, pageCount, setPageCount }
  const [searchQuery, setSearchQuery] = useState('')

  // Replace the searchInput state and effect with direct setSearchQuery
  const handleSearch = useCallback(
    (query: string) => {
      // Reset to first page when starting a new search from empty
      if (searchQuery === '' && query !== '') {
        setCurrentPage(1)
      }
      setSearchQuery(query)
    },
    [searchQuery]
  )

  // Replace the requestParams state and effect with a memoized value
  const requestParams = useMemo(
    () => ({
      environmentId: activeEnvironment.id,
      isSystem: agentTypeFilter === 'system',
      pageSize: 15,
      pageNumber: currentPage,
      filterByEnvId: true,
      // Add the search query to requestParams so it's included in the context provider
      ...(searchQuery ? { search: searchQuery } : {}),
    }),
    [agentTypeFilter, activeEnvironment.id, currentPage, searchQuery] // Include searchQuery in dependencies
  )

  const { toast } = useToast()
  const [resetCache, setResetCache] = useState(0)
  const [createAgent] = useCreateResource('AGENTS')
  const [deployingTeaser, setDeployingTeaser] = useState(false)
  const [showIcons, setShowIcons] = useState(false)
  const [visibleGroups, setVisibleGroups] = useState(libraryGroups)
  const [visibleTopics, setVisibleTopics] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [filteredLibrary, setFilteredLibrary] = useState(library)
  // Track deployed slugs at parent level to persist across remounts
  const [deployedSlugs, setDeployedSlugs] = useState<string[]>([])
  // Track slugs that are currently deploying
  const [deployingSlugs, setDeployingSlugs] = useState<string[]>([])
  const hasAgentsAccess = hasPermission({ resource: activeEnvironment, permissionKey: 'agent:read' })

  // Reset state when environment changes
  useEffect(() => {
    setDeployedSlugs([])
    setDeployingSlugs([])
    setResetCache((prev) => prev + 1) // Force AgentsCard components to refetch data
  }, [activeEnvironment.id])

  // Move widths calculation outside components
  const getColumnWidths = useCallback(() => {
    return showIcons
      ? {
          slug: '15%',
          id: '10%',
          namespace: '10%',
          deployed: '15%',
          event: '15%',
          topics: '30%',
          actions: '5%',
        }
      : {
          slug: '30%',
          id: '15%',
          namespace: '15%',
          deployed: '15%',
          event: '20%',
          actions: '5%',
        }
  }, [showIcons])

  const columnWidths = getColumnWidths()

  useEffect(() => {
    const filter = library
      .map((group: GroupType) => {
        const filteredAgents = group.agents.filter((agent: LibraryAgent) => {
          // Check if agent matches search query
          const matchesSearch =
            search.toLowerCase() === '' ||
            agent.title.toLowerCase().includes(search.toLowerCase()) ||
            agent.description.toLowerCase().includes(search.toLowerCase()) ||
            agent.slug.toLowerCase().includes(search.toLowerCase())

          // Check if agent matches selected topic filters
          const matchesTopics =
            visibleTopics.length === 0 ||
            visibleTopics.some((category) => agent.topics?.some((topic) => topic.includes(category)))

          // Check if group is in the selected groups
          const matchesGroup = visibleGroups.includes(group.name)

          // Return true if passes all active filters
          return matchesSearch && (matchesTopics || matchesGroup)
        })
        return filteredAgents.length > 0 ? { ...group, agents: filteredAgents } : null
      })
      .filter((group): group is GroupType => group !== null)

    setFilteredLibrary(filter)
  }, [visibleGroups, visibleTopics, search])

  // Update the handleRowActionSuccess to not set requestParams directly
  const handleRowActionSuccess = (action: string) => {
    if (action === 'DELETE') {
      setResetCache((currCacheKey) => currCacheKey + 1)
    }
  }

  const [teaserChecks, setTeaserChecks] = useState<{ [key: string]: boolean }>(
    teasers.reduce(
      (acc, t) => {
        acc[t.slug] = true
        return acc
      },
      {} as { [key: string]: boolean }
    )
  )

  const handleCheckboxChange = (slug: string) => {
    setTeaserChecks((prevState) => ({
      ...prevState,
      [slug]: !prevState[slug],
    }))
  }

  const handleDeployTeasers = async () => {
    const selectedSlugs = Object.entries(teaserChecks)
      .filter(([_, isChecked]) => isChecked)
      .map(([slug]) => slug)

    setDeployingTeaser(true)
    try {
      const deploymentPromises = selectedSlugs.map((slug) => {
        const agent = teasers.find((teaser) => teaser.slug === slug)
        return deployAgent(agent)
      })
      await Promise.all(deploymentPromises)
      toast({
        title: 'Success',
        description: 'Agents deployed successfully',
      })
    } catch (error) {
      logger.error('Failed to deploy agents:', error)
      toast({
        variant: 'destructive',
        title: 'Deployment Error',
        description: 'Failed to deploy one or more agents. Please try again.',
      })
    } finally {
      setDeployingTeaser(false)
      setActiveTab('deployed')
    }
  }

  const deployAgent = async (agent: any) => {
    try {
      // Add to deploying slugs
      setDeployingSlugs((prev) => [...prev, agent.slug])

      const response = await fetch(agent.dist)
      if (!response.ok) {
        throw new Error(`Failed to fetch agent source: ${response.statusText}`)
      }

      const file = await response.text()

      await createAgent({
        environmentId: activeEnvironment.id,
        agentConfig: {
          topics: agent.topics,
          compiler: 'js',
          source: file,
          slug: agent.slug,
        },
      })

      // Add to deployed slugs on success
      setDeployedSlugs((prev) => [...prev, agent.slug])
      setResetCache((prev) => prev + 1)
      return true
    } catch (error) {
      logger.error(`Failed to deploy agent ${agent.slug}:`, error)
      throw error
    } finally {
      // Remove from deploying slugs when done
      setDeployingSlugs((prev) => prev.filter((slug) => slug !== agent.slug))
    }
  }

  const AgentHeader = () => (
    <thead className='border-b border-gray-200'>
      <tr className='min-w-[780px]'>
        <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: columnWidths.slug }}>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Slug
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: columnWidths.id }}>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Agent ID
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: columnWidths.namespace }}>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Namespace
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: columnWidths.deployed }}>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Last Deployed
          </Text>
        </th>
        <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: columnWidths.event }}>
          <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
            Last Event
          </Text>
        </th>
        {showIcons && (
          <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: columnWidths.topics }}>
            <Text variant='subheadline' emphasized className='text-muted flex items-center h-full'>
              Topics
            </Text>
          </th>
        )}
        <th className='px-3 py-2 text-left align-middle h-[42px]' style={{ width: columnWidths.actions }}></th>
      </tr>
    </thead>
  )

  const AgentRow: ResourceRowComponent = ({ resourceId }) => {
    const agent: any = useResource('AGENTS', resourceId)
    const [deleteAgent] = useDeleteResource('AGENTS')
    const [openedDeleteAgent, setOpenedDeleteAgent] = useState(false)
    const [openedVersions, setOpenedVersions] = useState(false)
    const [openedDownload, setOpenedDownload] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    if (!agent) {
      return <></>
    }

    const handleDeleteClick = async () => {
      try {
        setIsDeleting(true)
        await deleteAgent({ agentId: agent.id, environmentId: activeEnvironment.id })
        toast({
          title: 'Success',
          description: `Agent ${agent.slug} deleted successfully`,
        })
        setOpenedDeleteAgent(false)

        // Update deployedSlugs state to remove the deleted agent
        setDeployedSlugs((prev) => prev.filter((slug) => slug !== agent.slug))

        // Refresh the agents collection so AgentsCard components update their deployment status
        getCollection('AGENTS', {
          environmentId: activeEnvironment.id,
          isSystem: false,
        }).refresh()
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: `Failed to delete agent ${agent.slug}`,
        })
        logger.error('Failed to delete agent:', error)
      } finally {
        setIsDeleting(false)
      }
    }

    const logs = useResourceList('AGENT_LOGS', { environmentId: agent.environmentId, agentId: resourceId }).response
      .data

    return (
      <tr className='hover:bg-gray-50 rounded-md overflow-hidden min-w-[780px]'>
        <td className='px-3 py-2 text-left align-middle border-b border-gray-200' style={{ width: columnWidths.slug }}>
          <Text>{agent.slug}</Text>
        </td>
        <td className='px-3 py-2 text-left align-middle border-b border-gray-200' style={{ width: columnWidths.id }}>
          <CustomCopyButton value={agent.id} />
        </td>
        <td
          className='px-3 py-2 text-left align-middle border-b border-gray-200'
          style={{ width: columnWidths.namespace }}
        >
          <Text className={`text-sm ${!agent.namespace ? 'text-muted italic' : ''}`}>{agent.namespace || '-'}</Text>
        </td>
        <td
          className='px-3 py-2 text-left align-middle border-b border-gray-200'
          style={{ width: columnWidths.deployed }}
        >
          <Text className='text-sm'>
            {new Date(agent.updatedAt).toLocaleDateString()} {new Date(agent.updatedAt).toLocaleTimeString()}
          </Text>
        </td>
        <td className='px-3 py-2 text-left align-middle border-b border-gray-200' style={{ width: columnWidths.event }}>
          <Text className={`text-sm ${!logs[0] ? 'text-muted italic' : ''}`}>
            {logs[0]
              ? `${new Date(logs[0].createdAt).toLocaleDateString()} ${new Date(logs[0].createdAt).toLocaleTimeString()}`
              : '-'}
          </Text>
        </td>
        {showIcons && (
          <td
            className='px-3 py-2 text-left align-middle border-b border-gray-200'
            style={{ width: columnWidths.topics }}
          >
            <div className='flex gap-2'>{getAgentIconSet(agent.topics)}</div>
          </td>
        )}
        <td
          className='px-3 py-2 text-left align-middle border-b border-gray-200'
          style={{ width: columnWidths.actions }}
        >
          {hasPermission({ resource: activeEnvironment, permissionKey: 'agent:delete' }) && agent && (
            <>
              <Dialog open={openedDeleteAgent} onOpenChange={(open) => !isDeleting && setOpenedDeleteAgent(open)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete {agent.slug} agent?</DialogTitle>
                  </DialogHeader>
                  <div className='space-y-4'>
                    <Text className='text-sm text-muted'>
                      Deleting this agent from the <strong>{activeEnvironment.name}</strong> environment cannot be
                      undone. Are you sure you want to proceed?
                    </Text>
                    <Button
                      variant='destructive'
                      className='w-full'
                      onClick={handleDeleteClick}
                      disabled={isDeleting}
                      icon={isDeleting ? 'loader' : undefined}
                      iconProps={isDeleting ? { className: 'animate-spin', size: 16 } : undefined}
                    >
                      {isDeleting ? 'Deleting...' : 'Yes, delete this agent'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-block cursor-pointer'>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant='ghost' size='icon-md' icon='dot-grid-1x3-vertical' iconProps={{ size: 12 }} />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align='end'>
                        <DropdownMenuItem onSelect={() => setOpenedVersions(true)}>Revert agent</DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setOpenedDownload(true)}>Download agent</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className='text-destructive' onSelect={() => setOpenedDeleteAgent(true)}>
                          Delete agent
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </span>
                </TooltipTrigger>
              </Tooltip>
            </>
          )}
          {hasPermission({ resource: activeEnvironment, permissionKey: 'agent:update' }) && agent && (
            <>
              <RevertVersionModal agent={agent} opened={openedVersions} onClose={() => setOpenedVersions(false)} />
              <DownloadAgentModal agent={agent} opened={openedDownload} onClose={() => setOpenedDownload(false)} />
            </>
          )}
        </td>
      </tr>
    )
  }

  // Create a new component to handle data fetching
  const AgentData = () => {
    // Use the context requestParams directly which now includes the search parameter
    const { resourceType, requestParams: agentDataRequestParams } = useContext(ResourceListContext)

    const {
      response: { data: agentData },
    } = useResourceList(resourceType, agentDataRequestParams)

    if (!agentData || agentData.length === 0) {
      // If a search is active and no results, show "No agents found"
      if (searchQuery) {
        return (
          <div className='flex justify-center items-center p-8'>
            <Text className='text-lg text-muted italic'>No agents found matching "{searchQuery}"</Text>
          </div>
        )
      }

      // If it's the system tab with no search, show the system message
      if (agentTypeFilter === 'system') {
        return (
          <div className='flex justify-center items-center p-8'>
            <Text className='text-lg text-muted italic'>No system agents found</Text>
          </div>
        )
      }

      // If no search on regular agents tab, show the library
      return (
        <div className='flex justify-center w-full'>
          <div className='w-full md:w-3/4 lg:w-2/3 max-w-3xl relative'>
            <div className='my-8'></div>
            <div className='space-y-4 mx-auto text-center'>
              <h4 className='text-center text-lg font-medium'>Welcome to your deployed agents</h4>
              <Text className='text-center text-sm text-muted'>
                Agents are deployed listeners that respond to events. <br />
                Here are a few popular agents to get you started.
              </Text>
            </div>

            <div className='my-6'></div>
            <div className='border border-gray-200 rounded-md mx-auto'>
              {teasers.map((teaser) => (
                <div key={teaser.slug} className='p-4 border-b border-gray-200 last:border-b-0'>
                  <div className='grid grid-cols-12 gap-3'>
                    <div className='col-span-1 flex items-start pt-1'>
                      <Checkbox
                        id={`teaser-${teaser.slug}`}
                        checked={teaserChecks[teaser.slug]}
                        onCheckedChange={() => handleCheckboxChange(teaser.slug)}
                      />
                    </div>
                    <div className='col-span-11 flex flex-col gap-1'>
                      <Text className='text-sm font-semibold'>{teaser.title}</Text>
                      <Text className='text-xs text-muted'>{teaser.description}</Text>
                    </div>
                  </div>
                </div>
              ))}
              <Separator />
              <div className='flex justify-center'>
                <Button
                  variant='ghost'
                  icon={deployingTeaser ? 'loader' : 'arrow-right'}
                  iconPlacement={deployingTeaser ? 'left' : 'right'}
                  iconProps={deployingTeaser ? { className: 'animate-spin', size: 16 } : { size: 16 }}
                  onClick={handleDeployTeasers}
                  className='py-3 px-4 hover:bg-gray-50'
                  disabled={deployingTeaser}
                >
                  <Text className='text-sm font-semibold'>
                    {deployingTeaser ? 'Deploying...' : 'Deploy selected agents'}
                  </Text>
                </Button>
              </div>
            </div>
            <div className='flex justify-center mt-6'>
              <Button
                variant='outline'
                icon='arrow-right'
                iconPlacement='right'
                iconProps={{ size: 16 }}
                onClick={() => {
                  setActiveTab('library')
                }}
              >
                <Text className='text-sm'>Find more agents in the library</Text>
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return (
      <ResourceList
        height={listHeight}
        errorMessage='Error loading agents.'
        emptyMessage='No agents found.'
        headerComponent={AgentHeader}
        rowComponent={AgentRow}
        onRowActionSuccess={handleRowActionSuccess}
        pagination={pagination}
        forceMount={resetCache}
      />
    )
  }

  const AgentsCard = ({ resource }: { resource: { slug: string; description: string } }) => {
    const agentsCardRequestParams = useMemo(
      () => ({
        environmentId: activeEnvironment.id,
        isSystem: false,
      }),
      [activeEnvironment.id, resetCache]
    )

    const deployedAgents = useCollection('AGENTS', agentsCardRequestParams)

    // Use either API data or parent tracking to determine deployed state
    const isDeployed =
      deployedAgents.some((agent) => agent?.slug === resource.slug) || deployedSlugs.includes(resource.slug)

    // Get deploying state from parent
    const isDeploying = deployingSlugs.includes(resource.slug)

    const deployThisAgent = async (agent: any) => {
      try {
        await deployAgent(agent)
        toast({
          title: 'Success',
          description: `Agent ${agent.slug} deployed successfully`,
        })
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Deployment Error',
          description: `Failed to deploy ${agent.slug}. Please try again.`,
        })
      }
    }

    return (
      <div className='p-4 border rounded-md shadow-sm relative w-full max-w-full hover:bg-gray-50'>
        <div className='space-y-4'>
          <div className='flex items-center justify-between'>
            <h5 className='text-base font-medium truncate'>{resource.slug || 'n/a'}</h5>
            {isDeployed && <Icon name='circle-check' size={16} className='text-primary flex-shrink-0 ml-2' />}
          </div>
          <Text className='text-xs text-muted line-clamp-2'>{resource.description}</Text>
          <Button
            variant='outline'
            className='w-full'
            onClick={() => deployThisAgent(resource)}
            disabled={isDeployed || isDeploying}
            icon={isDeploying ? 'loader' : undefined}
            iconProps={isDeploying ? { className: 'animate-spin', size: 16 } : undefined}
          >
            <Text className='text-xs'>{isDeployed ? 'Deployed' : isDeploying ? 'Deploying...' : 'Deploy agent'}</Text>
          </Button>
        </div>
      </div>
    )
  }

  const RevertVersionModal = ({
    agent,
    opened,
    onClose,
  }: {
    agent: Flatfile.Agent
    opened: boolean
    onClose: () => void
  }) => {
    const { data: versions } = useResourceList('AGENT_VERSIONS', agent.id).response
    const [revertToVersion] = useUpdateResource('AGENT_VERSIONS')
    const [version, setVersion] = useState<string | null>(null)
    const [selectedVersionLabel, setSelectedVersionLabel] = useState<string | null>(null)
    const { toast } = useToast()

    const dropdownData =
      versions?.map((v: any) => ({
        label: `${v.version}${
          v.description ? ` - ${v.description}` : ` - Created ${new Date(v.createdAt).toLocaleString()}`
        }`,
        value: v.id,
        disabled: v.version === Math.max(...versions.map((ver: any) => ver.version)),
      })) || []

    const singleVersion = versions.length === 1

    const handleRevert = useCallback(async () => {
      if (version) {
        try {
          const response = await revertToVersion({ agentId: agent.id, agentVersionId: version })
          if (response) {
            onClose()
            toast({
              title: 'Success',
              description: `Successfully reverted to agent version ${selectedVersionLabel}`,
            })
          }
        } catch (error) {
          toast({
            variant: 'destructive',
            title: 'Error',
            description: `Unable to revert to agent version ${selectedVersionLabel}`,
          })
          logger.error('Error reverting agent version:', error)
        }
      }
    }, [version, selectedVersionLabel, revertToVersion, agent.id, onClose, toast])

    return (
      <Dialog open={opened} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revert agent for {agent.slug}</DialogTitle>
          </DialogHeader>
          <Suspense
            fallback={
              <div className='flex justify-center py-4'>
                <Icon name='loader' size={18} className='animate-spin' />
              </div>
            }
          >
            {versions ? (
              <div className='space-y-4'>
                <Text className='text-sm'>
                  Each time you deploy or update an agent, we create a new version to track changes.
                </Text>
                <Text className='text-sm'>
                  Reverting allows you to restore your agent to a previous state. Select the version you'd like to use
                  from the list below.
                </Text>
                <div className='mt-2' />
                <Text className='text-sm font-bold text-destructive'>
                  Note: this will replace your current agent with the chosen version
                </Text>

                <div className='space-y-2'>
                  <Text className='text-sm font-medium'>Select the version you want to use</Text>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild disabled={singleVersion}>
                      <Button
                        variant='outline'
                        className='w-full justify-between'
                        icon='chevron-bottom'
                        iconPlacement='right'
                        iconProps={{ size: 12 }}
                        disabled={singleVersion}
                      >
                        {version
                          ? dropdownData.find((item) => item.value === version)?.label
                          : 'Choose a version to revert to'}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className='w-full max-h-[300px] overflow-y-auto'>
                      {dropdownData.map((option) => (
                        <DropdownMenuItem
                          key={option.value}
                          disabled={option.disabled}
                          onSelect={() => {
                            setVersion(option.value)
                            setSelectedVersionLabel(option.label)
                          }}
                        >
                          {option.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <Button className='w-full' onClick={handleRevert} disabled={!version}>
                  Revert {version && `to version ${selectedVersionLabel}`}
                </Button>
              </div>
            ) : (
              <Text>No versions available</Text>
            )}
          </Suspense>
        </DialogContent>
      </Dialog>
    )
  }

  // New component that contains both search controls and data
  const AgentControlsAndData = () => {
    const { resourceType, requestParams: agentDataRequestParams } = useContext(ResourceListContext)
    const {
      response: { data: agentData },
    } = useResourceList(resourceType, agentDataRequestParams)

    return (
      <>
        {agentData && agentData.length > 0 && (
          <div className='flex justify-between items-center py-2 px-2 gap-4 mb-2 bg-white'>
            <div className='relative'>
              <FocusedSearchBar
                onSearch={handleSearch}
                initialValue={searchQuery}
                placeholder='Search agents by slug or id...'
              />
            </div>
            <div className='flex items-center gap-4'>
              <div className='flex items-center gap-2'>
                <Switch id='show-icons' checked={showIcons} onCheckedChange={setShowIcons} />
                <label htmlFor='show-icons' className='text-sm whitespace-nowrap'>
                  Show topic icons
                </label>
              </div>
              <Pagination className='w-fit' iconOnly size='sm'>
                <PaginationContent>
                  <PaginationPrevious
                    onClick={() => {
                      if (currentPage > 1) setCurrentPage(currentPage - 1)
                    }}
                    disabled={currentPage <= 1}
                  />
                  {(() => {
                    const maxVisiblePages = 5
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
                    // Always show at least one page
                    const effectivePageCount = Math.max(1, pagination.pageCount)
                    const endPage = Math.min(effectivePageCount, startPage + maxVisiblePages - 1)

                    // Adjust start page if we're near the end
                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1)
                    }

                    // Generate array of page numbers to display
                    const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

                    return pages.map((pageNum) => (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          isActive={pageNum === currentPage}
                          onClick={() => setCurrentPage(pageNum)}
                          className={cn(
                            'w-5 h-5 flex items-center justify-center rounded-full p-0',
                            pageNum === currentPage
                              ? 'bg-surface-sentiment-subtle-neutral border-none'
                              : 'hover:bg-gray-100'
                          )}
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    ))
                  })()}
                  <PaginationNext
                    onClick={() => {
                      if (currentPage < pagination.pageCount) setCurrentPage(currentPage + 1)
                    }}
                    disabled={currentPage >= pagination.pageCount || pagination.pageCount <= 1}
                  />
                </PaginationContent>
              </Pagination>
            </div>
          </div>
        )}
        <div className='relative'>
          <div className='overflow-x-auto h-full relative'>
            <div className='min-w-[780px]'>
              <AgentData />
            </div>
          </div>
        </div>
      </>
    )
  }

  if (!hasAgentsAccess) return <NoAccessError page='agents' />
  return (
    <div ref={addToRefsArray} className='w-full h-full flex flex-col overflow-auto p-3'>
      <div className='p-3 flex justify-between'>
        <Text variant='title2'>Agents</Text>
        <TrialBanner />
      </div>
      <Tabs defaultValue={activeTab} className='w-full shadow-none rounded-none'>
        <TabsList className='gap-1 ml-2 bg-transparent p-0 min-h-[48px] w-full border-b border-table-border justify-start shadow-none rounded-none'>
          <TabsTrigger
            value='deployed'
            onClick={() => setActiveTab('deployed')}
            className='p-4 bg-transparent data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:rounded-none hover:text-primary min-h-[44px] w-fit !flex-grow-0 shadow-none rounded-none'
          >
            <span className='text-base font-medium'>Deployed Agents</span>
          </TabsTrigger>
          {hasPermission({ resource: activeEnvironment, permissionKey: 'agent:update' }) && (
            <TabsTrigger
              value='library'
              onClick={() => setActiveTab('library')}
              className='p-4 bg-transparent data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:rounded-none hover:text-primary min-h-[44px] w-fit !flex-grow-0 shadow-none rounded-none'
            >
              <span className='text-base font-medium'>Library</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value='deployed' className='mt-0 overflow-x-auto'>
          <div className='flex justify-between items-center py-4 px-2 gap-4'>
            <div className='flex items-center gap-4'>
              <div className='flex gap-4'>
                <Tabs value={agentTypeFilter} onValueChange={setAgentTypeFilter}>
                  <TabsList>
                    <TabsTrigger value='agents' className='px-8'>
                      Your Agents
                    </TabsTrigger>
                    <TabsTrigger value='system' className='px-8'>
                      System Agents
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
          </div>

          <div className='overflow-x-auto'>
            <ResourceListContextProvider requestParams={requestParams} resourceType='AGENTS'>
              <Suspense
                fallback={
                  <div className='flex justify-center items-center p-8'>
                    <Icon name='loader' className='animate-spin' size={18} />
                  </div>
                }
              >
                <AgentControlsAndData />
              </Suspense>
            </ResourceListContextProvider>
          </div>
        </TabsContent>

        <TabsContent value='library' className='mt-0 overflow-x-auto'>
          <div className='p-4'>
            <div className='flex items-center gap-4 flex-wrap'>
              <div className='relative'>
                <FocusedSearchBar onSearch={setSearch} initialValue={search} placeholder='Search agents' />
              </div>

              <div className='relative'>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='outline'
                      className='w-[200px] justify-between'
                      icon='chevron-bottom'
                      iconPlacement='right'
                      iconProps={{ size: 12 }}
                    >
                      Filter by category
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className='w-[200px]'>
                    {libraryGroups.map((group) => (
                      <DropdownMenuCheckboxItem
                        key={group}
                        checked={visibleGroups.includes(group)}
                        onCheckedChange={() => {
                          setVisibleGroups((prev) => {
                            if (prev.includes(group)) {
                              return prev.filter((g) => g !== group)
                            }
                            return [...prev, group]
                          })
                        }}
                      >
                        {group}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className='relative'>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='outline'
                      className='w-[200px] justify-between'
                      icon='chevron-bottom'
                      iconPlacement='right'
                      iconProps={{ size: 12 }}
                    >
                      Filter by topics
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className='w-[200px]'>
                    {eventTopics.map((topic) => (
                      <DropdownMenuCheckboxItem
                        key={topic}
                        checked={visibleTopics.includes(topic)}
                        onCheckedChange={() => {
                          setVisibleTopics((prev) => {
                            if (prev.includes(topic)) {
                              return prev.filter((t) => t !== topic)
                            }
                            return [...prev, topic]
                          })
                        }}
                      >
                        {topic}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {filteredLibrary.length > 0 ? (
            <div className='relative h-full'>
              <div className='overflow-x-auto h-full relative'>
                <div className='min-w-[780px]'>
                  {filteredLibrary.map((group) => (
                    <div className='px-4 mt-4' key={group.name}>
                      <h4 className='text-lg font-medium'>{group.name}</h4>
                      <Text className='text-sm'>{group.about}</Text>
                      <div className='my-4'></div>
                      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                        {group.agents.map((agent) => (
                          <AgentsCard key={agent.slug} resource={agent} />
                        ))}
                      </div>
                      <div className='my-4'></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className='flex justify-center items-center h-[50vh]'>
              <div className='text-center space-y-4 flex flex-col items-center'>
                <Text className='text-sm'>We didn't find anything matching your search params.</Text>
                <Button variant='outline' className='mx-auto' onClick={() => setVisibleGroups(libraryGroups)}>
                  See All
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
